import React, { useState, useEffect, useRef, useCallback } from 'react';
import EventModal from './EventModal';
import './EventsCarousel.css';

const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace('/api', '');
const AUTO_INTERVAL_MS = 4500;

const useItemsPerView = () => {
  const [items, setItems] = useState(4);
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      const portrait = window.innerHeight > window.innerWidth;
      if (portrait || w < 600) setItems(1);
      else if (w < 900) setItems(2);
      else if (w < 1200) setItems(3);
      else setItems(3);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return items;
};

const EventsCarousel = () => {
  const [events, setEvents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const itemsPerView = useItemsPerView();
  const intervalRef = useRef(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/events`)
      .then(r => r.json())
      .then(data => setEvents(data.events || []))
      .catch(() => {});
  }, []);

  const n = events.length;
  const maxIndex = Math.max(0, n - itemsPerView);

  useEffect(() => {
    setCurrentIndex(i => Math.min(i, maxIndex));
  }, [maxIndex]);

  const goNext = useCallback(() => {
    setCurrentIndex(i => (i >= maxIndex ? 0 : i + 1));
  }, [maxIndex]);

  const goPrev = useCallback(() => {
    setCurrentIndex(i => (i <= 0 ? maxIndex : i - 1));
  }, [maxIndex]);

  useEffect(() => {
    if (n <= itemsPerView) return;
    intervalRef.current = setInterval(goNext, AUTO_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [goNext, n, itemsPerView]);

  const pauseAndNav = (fn) => {
    clearInterval(intervalRef.current);
    fn();
    if (n > itemsPerView) {
      intervalRef.current = setInterval(goNext, AUTO_INTERVAL_MS);
    }
  };

  if (n === 0) return null;

  const showControls = n > itemsPerView;

  // When all items fit in view, centre them instead of sliding.
  const isCentered = n <= itemsPerView;

  // CSS-percentage approach: no JS pixel measurements needed.
  // Track width = (n / itemsPerView) × 100% of the viewport.
  // Each item = (100 / n)% of the track = (100 / itemsPerView)% of the viewport.
  // translateX by 1 step = (100 / n)% of track = (100 / itemsPerView)% of viewport.
  const trackStyle = isCentered
    ? { width: '100%', justifyContent: 'center' }
    : {
        width: `${(n / itemsPerView) * 100}%`,
        transform: `translateX(calc(${-currentIndex} * 100% / ${n}))`,
      };

  // Centred items use a fixed per-item fraction of the viewport; sliding items use track-relative %.
  const itemWidthPct = isCentered
    ? `calc(100% / ${itemsPerView})`
    : `${100 / n}%`;

  return (
    <section className="events-carousel-section">
      <div className="events-carousel-header">
        <h2 className="events-carousel-title">Current Events</h2>
      </div>

      <div className="events-carousel-wrapper">
        {showControls && (
          <button
            className="carousel-nav-btn carousel-nav-prev"
            onClick={() => pauseAndNav(goPrev)}
            aria-label="Previous"
          >
            &#8249;
          </button>
        )}

        <div className="events-carousel-viewport">
          <div className="events-carousel-track" style={trackStyle}>
            {events.map(event => (
              <div
                key={event.id}
                className="carousel-item"
                style={{ width: itemWidthPct }}
                onClick={() => setSelectedEvent(event)}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && setSelectedEvent(event)}
              >
                <div className="carousel-item-inner">
                  <div className="carousel-item-image">
                    {event.image_path
                      ? <img src={`${BACKEND_URL}${event.image_path}`} alt={event.title} />
                      : <span className="carousel-item-placeholder">📅</span>
                    }
                  </div>
                  <p className="carousel-item-title">{event.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {showControls && (
          <button
            className="carousel-nav-btn carousel-nav-next"
            onClick={() => pauseAndNav(goNext)}
            aria-label="Next"
          >
            &#8250;
          </button>
        )}
      </div>

      {showControls && (
        <div className="carousel-dots">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              className={`carousel-dot${i === currentIndex ? ' active' : ''}`}
              onClick={() => pauseAndNav(() => setCurrentIndex(i))}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}

      {selectedEvent && (
        <EventModal
          event={selectedEvent}
          backendUrl={BACKEND_URL}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </section>
  );
};

export default EventsCarousel;
